// hooks/useScreenSizes.ts

import { useMediaQuery } from '@mui/material'
import { Breakpoint, useTheme } from '@mui/material/styles'

/**
 * Checks if the screen is smaller than the tablet breakpoint.
 * @returns {boolean} True if the screen is smaller than the tablet breakpoint, false otherwise.
 */
export const useIsMobile = (): boolean => {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.down('tablet'))
}

/**
 * Checks if the screen is between the tablet and laptop breakpoints.
 * @returns {boolean} True if the screen is between the tablet and laptop breakpoints, false otherwise.
 */
export const useIsTablet = (): boolean => {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.between('tablet', 'laptop'))
}

/**
 * Checks if the screen is smaller than the laptop breakpoint.
 * @returns {boolean} True if the screen is smaller than the laptop breakpoint, false otherwise.
 */
export const useIsMobileAndTablet = (): boolean => {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.down('laptop'))
}

/**
 * Checks if the screen is larger than the laptop breakpoint.
 * @returns {boolean} True if the screen is larger than the laptop breakpoint, false otherwise.
 */
export const useIsDesktop = (): boolean => {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.up('laptop'))
}

/**
 * Checks if the screen is larger than the tablet breakpoint.
 * @returns {boolean} True if the screen is larger than the tablet breakpoint, false otherwise.
 */
export const useIsTabletUp = (): boolean => {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.up('tablet'))
}

/**
 * Checks if the screen is smaller than the desktop breakpoint.
 * @returns {boolean} True if the screen is smaller than the desktop breakpoint, false otherwise.
 */
export const useIsDesktopDown = (): boolean => {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.down('desktop'))
}
// General-purpose hook to determine if the screen matches a custom breakpoint
export const useBreakpoint = (breakpoint: string | number): boolean => {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.only(breakpoint as Breakpoint))
}
